import React from 'react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import AppCta from '../components/AppCta';
import useFadeIn from '../hooks/useFadeIn';
import { features, featureCategories } from '../data/features';

const propertyTypes = [
  { icon: 'fas fa-house-user', label: 'House' },
  { icon: 'fas fa-building', label: 'Apartment' },
  { icon: 'fas fa-city', label: 'Condominium' },
  { icon: 'fas fa-store', label: 'Retail' },
  { icon: 'fas fa-briefcase', label: 'Office' },
  { icon: 'fas fa-car', label: 'Parking' }
];

const paymentMethods = [
  { icon: 'fas fa-money-bill-wave', label: 'Cash' },
  { icon: 'fas fa-building-columns', label: 'Bank transfer' },
  { icon: 'fas fa-mobile-screen-button', label: 'E-wallet' },
  { icon: 'fas fa-credit-card', label: 'Credit / debit card' },
  { icon: 'fas fa-money-check', label: 'Check' }
];

const Features: React.FC = () => {
  useFadeIn();

  return (
    <div>
      <SEO
        title="Features: Rent, Tenant & Property Management Tools"
        description="Explore eTENANT features: properties and units, tenant profiles, AI-drafted rental agreements, rent tracking with PDF receipts, recurring charges, maintenance, and reports."
        path="/features"
        breadcrumb="Features"
      />

      <PageHero
        crumb="Features"
        eyebrow="Features"
        title={<>Every Tool You Need, <span>in One App</span></>}
        subtitle="From your first property to your hundredth tenant — eTENANT keeps rentals, rent, and paperwork organized on your phone."
      />

      {/* Category quick links */}
      <nav className="features-page-nav" aria-label="Feature categories">
        <div className="container">
          <div className="features-page-nav-list">
            {featureCategories.map((category) => (
              <a key={category.id} href={`#${category.id}`} className="features-page-chip">
                <i className={category.icon} aria-hidden="true"></i>
                {category.label}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* One block per category */}
      {featureCategories.map((category, catIndex) => {
        const items = features.filter((feature) => feature.category === category.id);
        return (
          <section
            key={category.id}
            id={category.id}
            className={`features-page-group${catIndex % 2 === 1 ? ' features-page-group-alt' : ''}`}
          >
            <div className="container">
              <div className="row g-4 g-lg-5">
                <div className="col-lg-4">
                  <div className="features-page-group-intro fade-in-up">
                    <span className="features-page-group-icon">
                      <i className={category.icon} aria-hidden="true"></i>
                    </span>
                    <span className="features-page-group-count">
                      {String(catIndex + 1).padStart(2, '0')} · {items.length} tools
                    </span>
                    <h2 className="features-page-group-title">{category.label}</h2>
                    <p className="features-page-group-blurb">{category.blurb}</p>
                  </div>
                </div>
                <div className="col-lg-8">
                  <div className="row g-4">
                    {items.map((feature, index) => (
                      <div key={feature.title} className="col-md-6">
                        <div className={`features-v2-card fade-in-up delay-${(index % 2) + 1}`}>
                          <div className="features-v2-card-top">
                            <span className="features-v2-icon">
                              <i className={feature.icon} aria-hidden="true"></i>
                            </span>
                            {feature.badge && <span className="features-v2-badge">{feature.badge}</span>}
                          </div>
                          <h3 className="features-v2-card-title">{feature.title}</h3>
                          <p className="features-v2-card-text">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Property types + payment methods */}
      <section className="features-page-fit">
        <div className="container">
          <div className="section-v2-header fade-in-up">
            <span className="features-v2-eyebrow">Built for how you rent</span>
            <h2 className="features-v2-title">
              Any Property. <span>Any Payment.</span>
            </h2>
            <p className="features-v2-subtitle">
              Manage every kind of rental and record rent the way your tenants actually pay.
            </p>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <div className="features-page-panel fade-in-up delay-1">
                <h3 className="features-page-panel-title">
                  <i className="fas fa-house-chimney" aria-hidden="true"></i>
                  6 property types
                </h3>
                <div className="features-page-tiles">
                  {propertyTypes.map((type) => (
                    <div key={type.label} className="features-page-tile">
                      <i className={type.icon} aria-hidden="true"></i>
                      <span>{type.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="features-page-panel fade-in-up delay-2">
                <h3 className="features-page-panel-title">
                  <i className="fas fa-receipt" aria-hidden="true"></i>
                  Flexible payment methods
                </h3>
                <div className="features-page-tiles">
                  {paymentMethods.map((method) => (
                    <div key={method.label} className="features-page-tile">
                      <i className={method.icon} aria-hidden="true"></i>
                      <span>{method.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AppCta
        eyebrow="Try every feature free"
        title="Start Managing Your Rentals Today"
        text="Download eTENANT from Google Play — use Guest Mode right away, then sign in anytime to sync across devices."
      />
    </div>
  );
};

export default Features;
